/**
 * RiderPreSettlementHistory Component
 * 라이더 선정산내역 페이지 (본사용)
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {useRiderPreSettlementData} from '../hooks/useRiderPreSettlementData';
import {useDelayedLoading} from '@/shared/hooks/useDelayedLoading';
import {useUserInfo} from '@/shared/hooks/useCrudOperations';
import {usePagination} from '@/shared/hooks/usePagination';
import {useColumnSort} from '@/shared/hooks/useTableSort';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import {loadFilters, saveFilters} from '@/shared/utils/filterStorage';
import {
    calculateInitialDateRange,
    validateFilters
} from '../lib/riderPreSettlementHelpers';
import FilterSection from './FilterSection';
import SettlementTable from './SettlementTable';
import {RiderPreSettlement} from '../types/riderPreSettlement.types';

import '@/shared/styles/common-table.css';

/** 필터 저장 키 */
const FILTER_STORAGE_KEY = 'riderPreSettlementFilters';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = 10;

// ========================================
// Types
// ========================================
interface FilterState {
    acctStDate: string;
    acctEdDate: string;
    riderName: string;
    supplierId: string;
}

// ========================================
// Component
// ========================================
const RiderPreSettlementHistory: React.FC = () => {
    // ========================================
    // Hooks & Context
    // ========================================
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    // ========================================
    // 사용자 정보 파생 상태
    // ========================================
    const userInfo = getUserInfo();
    const userType = userInfo?.userType;
    const isSupplierUser = userType === 'SUPPLIER';
    const isRiderUser = userType === 'RIDER';
    const userSupplierId = isSupplierUser ? userInfo?.userId : null;

    // ========================================
    // 데이터 훅 (skipInitialFetch를 항상 true로 설정)
    // ========================================
    const {
        settlements,
        suppliers,
        latestDate,
        loading,
        refreshSettlements,
        downloadExcel
    } = useRiderPreSettlementData(userSupplierId, true); // 항상 true로 변경

    const showLoading = useDelayedLoading(loading);

    // ========================================
    // 필터 상태
    // ========================================
    const [filters, setFilters] = useState<FilterState>(() => {
        // 협력사 사용자는 자신의 ID, 본사 사용자는 빈 문자열('')로 시작 (전체 의미)
        const initialSupplierId = isSupplierUser ? (userSupplierId || '') : '';
        
        const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
        return saved ? {
            acctStDate: saved.acctStDate || '',
            acctEdDate: saved.acctEdDate || '',
            riderName: saved.riderName || '',
            supplierId: initialSupplierId // 저장된 값 무시하고 항상 초기값 사용
        } : {
            acctStDate: '',
            acctEdDate: '',
            riderName: '',
            supplierId: initialSupplierId
        };
    });

    // ========================================
    // 정렬 훅 사용 (컬럼 인덱스 기반)
    // ========================================
    const getValueByIndex = useCallback((settlement: RiderPreSettlement, colIndex: number): string | number => {
        switch (colIndex) {
            case 1: return settlement.no;
            case 2: return settlement.supplierName;
            case 3: return settlement.riderId;
            case 4: return settlement.riderName;
            case 5: return settlement.riderType;
            case 6: return settlement.deliveryDate;
            case 7: return settlement.paymentDate;
            case 8: return settlement.processCount;
            case 9: return settlement.deliveryFee;
            case 10: return settlement.brokerManageFee;
            case 11: return settlement.partnerOperationFee;
            case 12: return settlement.totalOperationFee;
            case 13: return settlement.taxableDeliveryFee;
            case 14: return settlement.monthlySalary;
            case 15: return settlement.riderEmployment;
            case 16: return settlement.riderIndustrial;
            case 17: return settlement.hourlyInsurance;
            case 18: return settlement.insuranceTotal;
            case 19: return settlement.incomeTax;
            case 20: return settlement.localTax;
            case 21: return settlement.withholdingTax;
            case 22: return settlement.deductVal;
            case 23: return settlement.advanceSettlement;
            default: return 0;
        }
    }, []);

    const {
        sortConfig,
        sortedData: sortedSettlements,
        handleSort,
        resetSort
    } = useColumnSort<RiderPreSettlement>({
        data: settlements,
        getValueByIndex
    });

    // ========================================
    // 페이지네이션 (정렬된 데이터 사용)
    // ========================================
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: sortedSettlements, itemsPerPage: ITEMS_PER_PAGE });

    // ========================================
    // Refs
    // ========================================
    const hasAppliedInitialFilter = useRef(false);

    // 초기 로드 시 빈 결과 Toast 표시 여부 추적
    const hasShownInitialEmptyToast = useRef(false);

    // 초기 데이터 로드 완료 여부 (refreshSettlements의 결과를 받은 후 true)
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    // ========================================
    // Effects
    // ========================================

    /**
     * 필터 변경 시 로컬 스토리지에 저장 (본사 사용자는 협력사 ID 제외)
     */
    useEffect(() => {
        const filtersToSave = {
            acctStDate: filters.acctStDate,
            acctEdDate: filters.acctEdDate,
            riderName: filters.riderName,
            supplierId: isSupplierUser ? filters.supplierId : '' // 본사 사용자는 협력사 ID 저장 안함
        };
        saveFilters(FILTER_STORAGE_KEY, filtersToSave);
    }, [filters, isSupplierUser]);

    /**
     * 초기 로딩 시 기본값 설정 및 저장된 필터로 조회
     */
    useEffect(() => {
        // 이미 초기 필터가 적용되었으면 종료
        if (hasAppliedInitialFilter.current) {
            return;
        }
        
        // 기본 데이터 로딩이 완료될 때까지 대기
        if (loading) {
            return;
        }
        
        // 본사 사용자의 경우 suppliers가 로드되어야 함 (협력사 사용자는 예외)
        if (!isSupplierUser && suppliers.length === 0 && userSupplierId !== null) {
            return;
        }

        // 협력사 사용자 또는 본사 사용자의 협력사 ID 결정
        const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
        const supplierId = isSupplierUser
            ? (userSupplierId || '')
            : ''; // 본사 사용자는 빈 문자열 (전체)
            
        // 협력사 ID가 없으면 대기 (협력사 사용자만 해당)
        if (isSupplierUser && !supplierId) {
            return;
        }

        // 초기 필터 적용 플래그 설정 (중복 실행 방지)
        hasAppliedInitialFilter.current = true;

        // 저장된 필터가 있으면 해당 필터로 조회
        if (saved?.acctStDate && saved?.acctEdDate) {
            setFilters({
                acctStDate: saved.acctStDate,
                acctEdDate: saved.acctEdDate,
                riderName: saved.riderName || '',
                supplierId: supplierId
            });

            refreshSettlements(
                saved.acctStDate,
                saved.acctEdDate,
                saved.riderName || undefined,
                supplierId || undefined
            ).then(() => {
                setInitialDataLoaded(true);
            });
        } else {
            // 저장된 필터가 없으면 최근 7일 기준으로 조회
            const dateToUse = latestDate || new Date().toISOString().split('T')[0];
            const { startDate, endDate } = calculateInitialDateRange(dateToUse);

            setFilters({
                acctStDate: startDate,
                acctEdDate: endDate,
                riderName: '',
                supplierId: supplierId
            });

            refreshSettlements(
                startDate,
                endDate,
                undefined,
                supplierId || undefined
            ).then(() => {
                setInitialDataLoaded(true);
            });
        }
    }, [loading, suppliers, latestDate, isSupplierUser, userSupplierId, refreshSettlements]);

    /**
     * 초기 로드 완료 후 결과가 없으면 Toast 표시
     * initialDataLoaded가 true로 변경되는 시점에만 체크 (검색 시에는 handleSearch에서 처리)
     */
    useEffect(() => {
        if (!initialDataLoaded) return;
        if (hasShownInitialEmptyToast.current) return;
        if (loading) return; // 로딩 중에는 체크하지 않음

        // 초기 로드 완료 시점에 결과가 0건인 경우에만 Toast 표시
        if (settlements.length === 0) {
            showToast('조회된 정산내역이 없습니다.', { type: ToastType.INFO });
        }
        hasShownInitialEmptyToast.current = true;
    }, [initialDataLoaded, loading, settlements.length, showToast]);

    // ========================================
    // Handlers
    // ========================================

    /**
     * 필터 값 변경 핸들러
     */
    const handleFilterChange = useCallback((key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    /**
     * 검색 버튼 클릭 핸들러
     */
    const handleSearch = useCallback(async () => {
        const validation = validateFilters(filters.acctStDate, filters.acctEdDate, filters.supplierId);
        if (!validation.isValid) {
            showToast(validation.message!, { type: ToastType.WARNING });
            return;
        }

        const resultCount = await refreshSettlements(
            filters.acctStDate,
            filters.acctEdDate,
            filters.riderName || undefined,
            filters.supplierId || undefined
        );
        resetPagination();

        // 조회 결과가 0건인 경우 Toast 메시지 표시
        if (resultCount === 0) {
            showToast('조회된 정산내역이 없습니다.', { type: ToastType.INFO });
        }
    }, [filters, refreshSettlements, resetPagination, showToast]);

    /**
     * 초기화 버튼 클릭 핸들러
     * 페이지 진입 시 상태로 필터 및 정렬 초기화
     */
    const handleReset = useCallback(() => {
        // 기본 협력사 설정: 본사 사용자는 빈 문자열('') (전체), 협력사 사용자는 자신의 ID
        const initialSupplierId = isSupplierUser
            ? (userSupplierId || '')
            : ''; // 본사 사용자는 빈 문자열 (전체)

        // 초기 날짜 범위 계산
        const dateToUse = latestDate || new Date().toISOString().split('T')[0];
        const { startDate, endDate } = calculateInitialDateRange(dateToUse);

        // 필터 초기화
        setFilters({
            acctStDate: startDate,
            acctEdDate: endDate,
            riderName: '',
            supplierId: initialSupplierId
        });

        // 정렬 초기화
        resetSort();

        // 페이지네이션 초기화
        resetPagination();

        // 초기 상태로 데이터 조회
        if (startDate && endDate) {
            refreshSettlements(
                startDate,
                endDate,
                undefined,
                initialSupplierId || undefined
            );
        }
    }, [isSupplierUser, userSupplierId, latestDate, resetPagination, refreshSettlements, resetSort]);

    /**
     * 엑셀 다운로드 핸들러
     */
    const handleExcelDownload = useCallback(async () => {
        const validation = validateFilters(filters.acctStDate, filters.acctEdDate, filters.supplierId);
        if (!validation.isValid) {
            showToast(validation.message!, { type: ToastType.WARNING });
            return;
        }

        await downloadExcel(
            filters.acctStDate,
            filters.acctEdDate,
            filters.riderName || undefined,
            filters.supplierId || undefined
        );
    }, [filters, downloadExcel, showToast]);

    // ========================================
    // Render - Loading
    // ========================================
    if (showLoading) {
        return <Loading message="라이더 선정산내역을 불러오는 중..." />;
    }

    // ========================================
    // Render - Main
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">라이더 선정산내역</h1>
            </div>

            {/* 검색 필터 영역 */}
            <FilterSection
                filters={filters}
                suppliers={suppliers}
                isSupplierUser={isSupplierUser}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onReset={handleReset}
                onExcelDownload={handleExcelDownload}
            />

            {/* 테이블 영역 */}
            <SettlementTable
                settlements={sortedSettlements}
                currentItems={currentItems}
                currentPage={currentPage}
                loading={loading}
                isRiderUser={isRiderUser}
                sortConfig={sortConfig}
                onSort={handleSort}
            />

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default RiderPreSettlementHistory;
